import posthog from "posthog-js";

type TokenProvider = () => Promise<string | null>;

type RequestConfig = Omit<RequestInit, "headers"> & {
  headers?: HeadersInit;
  auth?: boolean;
};

type ApiClientConfig = {
  baseUrl?: string;
  getToken?: TokenProvider;
};

export const POSTHOG_DISTINCT_ID_HEADER = "X-PostHog-Distinct-Id";
export const POSTHOG_SESSION_ID_HEADER = "X-PostHog-Session-Id";

function normalizeAnalyticsHeaderValue(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.trim();
  return normalized ? normalized.slice(0, 255) : undefined;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail?: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

function getBaseUrl(override?: string) {
  const baseUrl = override ?? process.env.NEXT_PUBLIC_API_ENDPOINT;
  if (!baseUrl) {
    throw new Error("Missing NEXT_PUBLIC_API_ENDPOINT");
  }
  return baseUrl;
}

export function withAnalyticsHeaders(headersInit?: HeadersInit) {
  const headers = new Headers(headersInit);

  if (typeof window === "undefined") {
    return headers;
  }

  const distinctId = normalizeAnalyticsHeaderValue(posthog.get_distinct_id?.());
  if (distinctId && !headers.has(POSTHOG_DISTINCT_ID_HEADER)) {
    headers.set(POSTHOG_DISTINCT_ID_HEADER, distinctId);
  }

  const sessionId = normalizeAnalyticsHeaderValue(posthog.get_session_id?.());
  if (sessionId && !headers.has(POSTHOG_SESSION_ID_HEADER)) {
    headers.set(POSTHOG_SESSION_ID_HEADER, sessionId);
  }

  return headers;
}

async function parseResponse(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");

  if (!isJson) {
    return response.text();
  }

  return response.json();
}

async function applyAuth(headers: Headers, getToken: TokenProvider | undefined, authEnabled: boolean): Promise<void> {
  if (!authEnabled) return;
  if (!getToken) {
    throw new Error("Auth requested but no token provider configured");
  }
  const token = await getToken();
  if (!token) {
    throw new Error("Unable to resolve auth token");
  }
  headers.set("Authorization", `Bearer ${token}`);
}

export function createApiClient(config: ApiClientConfig = {}) {
  const baseUrl = getBaseUrl(config.baseUrl);

  async function request<T>(path: string, init: RequestConfig = {}): Promise<T> {
    const headers = withAnalyticsHeaders(init.headers);

    if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }

    await applyAuth(headers, config.getToken, init.auth ?? false);

    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
      cache: init.cache ?? "no-store",
    });

    const payload = await parseResponse(response);

    if (!response.ok) {
      const detail =
        typeof payload === "object" && payload !== null && "detail" in payload ? (payload as { detail?: unknown }).detail : undefined;
      const message =
        typeof detail === "string"
          ? detail
          : typeof detail === "object" &&
              detail !== null &&
              "message" in detail &&
              typeof (detail as { message?: unknown }).message === "string"
            ? (detail as { message: string }).message
            : `Request failed (${response.status})`;
      throw new ApiError(message, response.status, payload);
    }

    return payload as T;
  }

  return {
    request,
    get<T>(path: string, init?: RequestConfig) {
      return request<T>(path, { ...init, method: "GET" });
    },
    post<T>(path: string, body?: BodyInit | object, init?: RequestConfig) {
      const resolvedBody =
        body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob)
          ? JSON.stringify(body)
          : (body as BodyInit | undefined);
      return request<T>(path, { ...init, method: "POST", body: resolvedBody });
    },
    put<T>(path: string, body?: BodyInit | object, init?: RequestConfig) {
      const resolvedBody =
        body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob)
          ? JSON.stringify(body)
          : (body as BodyInit | undefined);
      return request<T>(path, { ...init, method: "PUT", body: resolvedBody });
    },
    patch<T>(path: string, body?: BodyInit | Record<string, unknown>, init?: RequestConfig) {
      const resolvedBody =
        body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob)
          ? JSON.stringify(body)
          : (body as BodyInit | undefined);
      return request<T>(path, { ...init, method: "PATCH", body: resolvedBody });
    },
    delete<T>(path: string, init?: RequestConfig) {
      return request<T>(path, { ...init, method: "DELETE" });
    },
    async stream(path: string, onChunk: (chunk: string) => void, init: RequestConfig = {}) {
      const headers = withAnalyticsHeaders(init.headers);

      await applyAuth(headers, config.getToken, init.auth ?? false);

      const response = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers,
        cache: init.cache ?? "no-store",
      });

      if (!response.ok) {
        throw new ApiError(`Request failed (${response.status})`, response.status);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No stream reader available");
      }

      const decoder = new TextDecoder("utf-8");
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        onChunk(decoder.decode(value, { stream: true }));
      }
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
